import type {
  AgenticCartBundleSummaryResponse,
  CreateAgenticCartOrderResponse,
} from "@ishvaram/api-client";
import { tokens } from "@ishvaram/ui/tokens";
import { rememberCartConfirmation } from "./AgenticCartCheckout.confirmation";
import { loadRazorpayScript } from "@/components/PaidReportCTA.payment";
import { getGoogleAnalyticsClientId } from "@/lib/analytics-ga4";
import { verifyAgenticCartPayment } from "@/lib/api/plans";
import { markCheckoutInFlight, resolveCheckout } from "@/lib/checkout-abandon-fallback";
import type {
  AgenticCartCallbacks,
  ContactForm,
  RazorpaySuccessResponse,
  SuccessState,
} from "./AgenticCartCheckout.types";
import {
  clearCartCheckoutRecoveryContext,
  recordAgenticCartAbandon,
} from "./AgenticCartCheckout.recovery";
import {
  AGENTIC_CART_CHECKOUT_CHANNEL,
  AGENTIC_CART_PLACEMENT,
  AGENTIC_CART_SELECTED_OFFER_REASON,
  AGENTIC_CART_SOURCE,
} from "./AgenticCartCheckout.attribution";
import {
  recordNativeCheckoutAbandoned,
  recordNativePurchaseCompleted,
  trackNativeRazorpayClosed,
  trackNativeRazorpayOpened,
} from "./AgenticCartCheckout.telemetry";

type RazorpayContext = AgenticCartCallbacks & {
  bundle: AgenticCartBundleSummaryResponse;
  order: CreateAgenticCartOrderResponse;
  contact: ContactForm;
  sourcePath: string;
  intent: string;
  customerProblem: string;
  agentTraceId?: string;
  journeyActionId?: string;
  concernId?: string;
  reportInputs: Record<string, unknown>;
  language?: "en" | "hi";
  /** Pre-checked WhatsApp opt-in — read by recordAgenticCartAbandon for
   *  recovery_phone_consent on the abandon payload. */
  whatsappConsent?: boolean;
  eventId: string;
};

export function openRazorpay(ctx: RazorpayContext) {
  return loadRazorpayScript().then(() => {
    if (!window.Razorpay) throw new Error("Razorpay unavailable");
    const orderId = ctx.order.razorpay_order_id;
    let abandonHandled = false;
    // Fires at most once per in-flight checkout — on modal dismiss OR on the
    // browser-unload failsafe, whichever comes first. Mirrors the report-CTA
    // flow: persist recovery, surface the resume affordance, notify the backend.
    const fireAbandon = () => {
      if (abandonHandled) return;
      abandonHandled = true;
      resolveCheckout(orderId);
      recordAgenticCartAbandon(ctx, ctx.onRecovery);
      recordNativeCheckoutAbandoned(ctx);
    };
    markCheckoutInFlight({ orderId, fireAbandon });
    const settle = () => {
      abandonHandled = true;
      resolveCheckout(orderId);
    };
    const wasSettled = () => abandonHandled;
    let paymentReported = false;
    const accept = () => {
      if (paymentReported) return false;
      paymentReported = true;
      return true;
    };
    const rzp = new window.Razorpay(razorpayOptions(ctx, fireAbandon, settle, wasSettled, accept));
    rzp.on?.("payment.failed", () => {
      if (paymentReported) return;
      // A failed payment is terminal for the abandon path (the user can still
      // retry inside the modal); don't double-fire recovery on the later dismiss.
      settle();
      ctx.onError("Payment failed. Please try again or use another method.");
      ctx.onStatus("ready");
      trackNativeRazorpayClosed(ctx, "payment_failed");
    });
    trackNativeRazorpayOpened(ctx);
    rzp.open();
  });
}

function razorpayOptions(
  ctx: RazorpayContext,
  fireAbandon: () => void,
  settle: () => void,
  abandonWasSettled: () => boolean,
  accept: () => boolean
): Record<string, unknown> {
  return {
    key: ctx.order.key_id,
    amount: ctx.order.amount_paisa,
    currency: ctx.order.currency,
    name: "Ishvaram Jyotish",
    description: ctx.bundle.name,
    order_id: ctx.order.razorpay_order_id,
    method: { upi: true, card: true, netbanking: false, wallet: false, paylater: false },
    config: {
      display: {
        preferences: { show_default_blocks: true },
        hide: [{ method: "wallet" }, { method: "netbanking" }, { method: "paylater" }],
      },
    },
    timeout: 900,
    retry: { enabled: true, max_count: 3 },
    remember_customer: true,
    prefill: razorpayPrefill(ctx.contact),
    notes: {
      kind: AGENTIC_CART_SOURCE,
      cart_id: ctx.order.cart_id,
      bundle_id: ctx.bundle.bundle_id,
      checkout_channel: AGENTIC_CART_CHECKOUT_CHANNEL,
      source_path: ctx.sourcePath,
    },
    theme: { color: tokens.color.shared.accent },
    handler: (response: RazorpaySuccessResponse) => {
      if (response.razorpay_order_id !== ctx.order.razorpay_order_id || !accept()) return;
      // Success path is terminal — stop the abandon failsafe before verifying.
      settle();
      if (!ctx.bundle.requires_shipping && !ctx.bundle.sankalp_program) {
        rememberCartConfirmation({
          bundleId: ctx.bundle.bundle_id,
          order: ctx.order,
          language: ctx.language ?? "en",
        });
      }
      ctx.onStatus("verifying");
      void handlePaymentSuccess(ctx, response);
    },
    modal: {
      ondismiss: () => {
        if (abandonWasSettled()) return;
        ctx.onStatus("ready");
        fireAbandon();
      },
    },
  };
}

function razorpayPrefill(contact: ContactForm) {
  return {
    name: contact.name.trim(),
    email: contact.email.trim(),
    contact: contact.phone.trim(),
  };
}

async function handlePaymentSuccess(ctx: RazorpayContext, response: RazorpaySuccessResponse) {
  try {
    const result = await verifyAgenticCartPayment({
      cart_id: ctx.order.cart_id,
      bundle_id: ctx.bundle.bundle_id,
      razorpay_order_id: response.razorpay_order_id,
      razorpay_payment_id: response.razorpay_payment_id,
      razorpay_signature: response.razorpay_signature,
      customer_email: ctx.contact.email.trim(),
      customer_phone: ctx.contact.phone.trim(),
      report_inputs: ctx.reportInputs,
      source: AGENTIC_CART_SOURCE,
      placement: AGENTIC_CART_PLACEMENT,
      source_path: ctx.sourcePath,
      intent: ctx.intent,
      customer_problem: ctx.customerProblem,
      journey_action_id: ctx.journeyActionId,
      concern_id: ctx.concernId,
      agent_trace_id: ctx.agentTraceId,
      selected_offer_reason: AGENTIC_CART_SELECTED_OFFER_REASON,
      ga_client_id: getGoogleAnalyticsClientId(),
      event_id: ctx.eventId,
    });
    if (!result.success) {
      ctx.onError("Payment confirmation is pending. Do not pay again. Contact support.");
      ctx.onStatus("verification_pending");
      return;
    }
    completeSuccess(ctx, { order: ctx.order, result });
  } catch {
    ctx.onError("Payment confirmation is pending. Do not pay again. Contact support.");
    ctx.onStatus("verification_pending");
  }
}

function completeSuccess(ctx: RazorpayContext, success: SuccessState) {
  // Clear any recovery context saved by a prior abandon on this bundle so a
  // returning visitor isn't nudged to "resume" a checkout they completed.
  clearCartCheckoutRecoveryContext();
  ctx.onSuccess(success);
  ctx.onStatus("success");
  recordNativePurchaseCompleted(ctx);
}
